<template>
  <v-container>
    <v-responsive>
      <h1 class="ma-6">Zuständigkeiten</h1>
      <v-row class="ma-2">
        <div class="col-sm-3 custom-col-menu">
          <MainSidebar />
        </div>
        <div class="col-sm-9 custom-col-main">
          <!-- Area Selection Card -->
          <v-card tile class="pa-2">
            <v-card-text>
              <v-select
                v-model="selectedArea"
                :items="areas"
                :loading="!areas.length"
                outlined
                dense
                flat
                hide-details
                item-text="name"
                item-value="id"
                label="Gebiet auswählen"
                no-data-text="Daten laden..."
                return-object
                @change="onAreaChange"
              />
            </v-card-text>

            <!-- Filters -->
            <v-card-text>
              <v-row>
                <v-col cols="12" sm="4">
                  <v-select
                    v-model="filters.businessUnit"
                    :items="businessUnitNames"
                    outlined
                    dense
                    flat
                    clearable
                    no-data-text="Daten laden..."
                    hint="Geschäftsfeld suchen"
                    persistent-hint
                    @click:clear="onBusinessUnitClear"
                  />
                </v-col>
                <v-col cols="12" sm="4">
                  <v-select
                    v-model="filters.task"
                    :items="taskNames"
                    outlined
                    dense
                    flat
                    clearable
                    no-data-text="Daten laden..."
                    hint="Tätigkeit suchen"
                    persistent-hint
                  />
                </v-col>
                <v-col cols="12" sm="4">
                  <v-text-field
                    v-model="filters.person"
                    outlined
                    dense
                    flat
                    clearable
                    hint="Name oder Kürzel suchen"
                    persistent-hint
                  />
                </v-col>
              </v-row>
            </v-card-text>
          </v-card>

          <!-- Loading State -->
          <v-card v-if="loading" flat class="mt-4 pa-2">
            <v-card-text class="text-center">
              <v-progress-circular indeterminate color="primary" />
            </v-card-text>
          </v-card>

          <!-- Results -->
          <v-card v-else elevation="1" outlined tile class="mt-4 pa-2" flat>
            <v-card-title>
              <v-icon large class="mr-4">mdi-format-list-bulleted-square</v-icon>
              RESULTAT
            </v-card-title>

            <v-card-text v-if="filteredData.length">
              <v-list dense nav expand>
                <v-list-group
                  v-for="businessUnit in filteredData"
                  :key="businessUnit.id"
                  :value="shouldExpandGroups"
                  active-class="grey lighten-4"
                  no-action
                >
                  <template v-slot:activator>
                    <v-list-item-content>
                      <v-list-item-title class="ingesa_headline font-weight-bold">
                        {{ businessUnit.business_unit_name.toUpperCase() }}
                      </v-list-item-title>
                    </v-list-item-content>
                  </template>

                  <v-list-group
                    v-for="task in businessUnit.responsibilities"
                    :key="task.id"
                    :value="shouldExpandGroups"
                    no-action
                    sub-group
                  >
                    <template v-slot:activator>
                      <v-list-item-content>
                        <v-list-item-title class="ingesa_sublines">
                          {{ task.task }}
                        </v-list-item-title>
                      </v-list-item-content>
                    </template>

                    <v-row dense class="ma-2 ml-15">
                      <v-col
                        v-for="(person, index) in task.persons"
                        :key="person.id"
                        :class="getPersonClass(task.persons.length, index)"
                        cols="12"
                        md="6"
                        sm="12"
                      >
                        <PersonCard
                          :person="person.details"
                          :is-main="person.is_main"
                        />
                      </v-col>
                    </v-row>
                  </v-list-group>
                </v-list-group>
              </v-list>
            </v-card-text>

            <v-card-text v-else>
              Diese Filterkombination ergibt keine Ergebnisse.
            </v-card-text>
          </v-card>
        </div>
      </v-row>
    </v-responsive>
  </v-container>
</template>

<script>
import { mapState, mapActions } from 'vuex'
import MainSidebar from './MainSidebar.vue'
import PersonCard from './PersonCard.vue'

export default {
  name: 'ZklList',
  
  components: { 
    MainSidebar, 
    PersonCard 
  },

  data() {
    return {
      loading: false,
      selectedArea: null,
      filters: {
        businessUnit: '',
        task: '',
        person: '',
      },
    }
  },

  computed: {
    ...mapState('areas', ['areas']),
    ...mapState('listData', ['listData']),
    ...mapState('persons', ['allPersons']),

    /**
     * Enriches list data with updated person details from allPersons store
     */
    enrichedListData() {
      if (!this.listData?.length || !this.allPersons?.length) {
        return []
      }

      return this.listData.map(businessUnit => ({
        ...businessUnit,
        responsibilities: this.enrichResponsibilities(businessUnit.responsibilities),
      }))
    },

    /**
     * Available business unit names for filter dropdown
     */
    businessUnitNames() {
      return [...new Set(
        this.enrichedListData.map(bu => bu.business_unit_name)
      )].sort((a, b) => a.localeCompare(b))
    },

    /**
     * Available task names for filter dropdown
     * Filtered by selected business unit if applicable
     */
    taskNames() {
      const tasks = this.filters.businessUnit
        ? this.getTasksForBusinessUnit(this.filters.businessUnit)
        : this.getAllTasks()

      return [...new Set(tasks)].sort((a, b) => a.localeCompare(b))
    },

    /**
     * Determines if list groups should be expanded based on active filters
     */
    shouldExpandGroups() {
      return !!(
        this.filters.businessUnit ||
        this.filters.task ||
        this.filters.person
      )
    },

    /**
     * Applies all active filters to the enriched data
     */
    filteredData() {
      let result = this.enrichedListData

      if (this.filters.businessUnit) {
        result = this.filterByBusinessUnit(result)
      }

      if (this.filters.task) {
        result = this.filterByTask(result)
      }

      if (this.filters.person) {
        result = this.filterByPerson(result)
      }

      return this.sortData(result)
    },
  },

  watch: {
    async selectedArea(newArea) {
      if (!newArea) return

      this.loading = true
      try {
        await this.fetchListData(newArea.id)
      } catch (error) {
        console.error('Error fetching list data:', error)
      } finally {
        this.loading = false
      }
    },
  },

  created() {
    this.getAreas({ only_active: true })
  },

  mounted() {
    this.fetchAllPersonsInBackground()
  },

  methods: {
    ...mapActions('listData', ['fetchListData']),
    ...mapActions('areas', ['getAreas']),
    ...mapActions('persons', ['fetchAllPersonsInBackground']),

    /**
     * Enriches responsibilities with updated person details
     */
    enrichResponsibilities(responsibilities) {
      return responsibilities.map(task => ({
        ...task,
        persons: this.enrichPersons(task.persons),
      }))
    },

    /**
     * Updates person details from allPersons store
     */
    enrichPersons(persons) {
      return persons.map(person => {
        const updatedDetails = this.allPersons.find(
          p => p.email === person.details?.email
        )
        return updatedDetails
          ? { ...person, details: updatedDetails }
          : person
      })
    },

    /**
     * Gets all tasks for a specific business unit
     */
    getTasksForBusinessUnit(businessUnitName) {
      const businessUnit = this.enrichedListData.find(
        bu => bu.business_unit_name === businessUnitName
      )
      return businessUnit
        ? businessUnit.responsibilities.map(r => r.task)
        : []
    },

    /**
     * Gets all tasks from all business units
     */
    getAllTasks() {
      return this.enrichedListData.flatMap(bu =>
        bu.responsibilities.map(r => r.task)
      )
    },

    /**
     * Filter data by business unit name
     */
    filterByBusinessUnit(data) {
      return data.filter(bu =>
        bu.business_unit_name
          .toLowerCase()
          .includes(this.filters.businessUnit.toLowerCase())
      )
    },

    /**
     * Filter data by task name
     */
    filterByTask(data) {
      return data
        .map(bu => ({
          ...bu,
          responsibilities: bu.responsibilities.filter(task =>
            task.task
              .toLowerCase()
              .includes(this.filters.task.toLowerCase())
          ),
        }))
        .filter(bu => bu.responsibilities.length > 0)
    },

    /**
     * Filter data by person name, surname, or short code
     */
    filterByPerson(data) {
      const searchTerm = this.filters.person.toLowerCase()

      return data
        .map(bu => ({
          ...bu,
          responsibilities: bu.responsibilities
            .map(task => ({
              ...task,
              persons: task.persons.filter(person =>
                this.matchesPerson(person, searchTerm)
              ),
            }))
            .filter(task => task.persons.length > 0),
        }))
        .filter(bu => bu.responsibilities.length > 0)
    },

    /**
     * Checks if a person matches the search term
     */
    matchesPerson(person, searchTerm) {
      const searchFields = ['name', 'surname', 'short']
      return searchFields.some(field =>
        person.details?.[field]?.toLowerCase().includes(searchTerm)
      )
    },

    /**
     * Sorts data by business unit name and tasks
     */
    sortData(data) {
      return data
        .map(bu => ({
          ...bu,
          responsibilities: [...bu.responsibilities].sort((a, b) =>
            a.task.localeCompare(b.task)
          ),
        }))
        .sort((a, b) =>
          a.business_unit_name.localeCompare(b.business_unit_name)
        )
    },

    /**
     * Handles area selection change
     */
    onAreaChange(area) {
      this.resetFilters()
      this.$emit('area-change', area)
    },

    /**
     * Handles business unit filter clear
     */
    onBusinessUnitClear() {
      this.filters.task = ''
    },

    resetFilters() {
      this.filters.businessUnit = ''
      this.filters.task = ''
      this.filters.person = ''
    },

    getPersonClass(personCount, index) {
      const baseClass = 'd-flex max-height'
      const isSmallScreen = this.$vuetify.breakpoint.smAndDown

      if (!isSmallScreen) {
        return baseClass
      }

      if (personCount === 1) {
        return `${baseClass} mb-0 pt-0`
      }

      const isLastPerson = index === personCount - 1
      return isLastPerson
        ? `${baseClass} pt-0 mt-7`
        : `${baseClass} pt-0`
    },
  },
}
</script>
